import { useState, useEffect } from 'react';
import { calculateDifference, formatFromAndToQuery, formatString, handleExport } from '../lib';
import { useData } from '../../utils/hooks/useData';
import { humanizeCamelCase } from '../../utils/StringFormatters.js';

import AnalyticsError from '../AnalyticsError';
import StandardLayout from '../../layout/StandardLayout';
import DateRangeComparatorInput from '../inputs/DateRangeComparatorInput';
import TableSegmentSearch from './_components/TableSegmentSearch';
import TextInput from '../../inputs/form-inputs/TextInput.js';
import { AnalyticsStoreProvider, useAnalyticsStoreContext } from '../contexts.js';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const DEFAULT_DATA = {
  dimension_headers: [],
  metric_headers: [],
  rows: [],
};

function Table({ endpoints, report, report_id }) {
  const { report_api_v1_cms_analytic_path, api_v1_cms_data_exports_path } = endpoints;

  const [isExporting, setIsExporting] = useState(false);

  const numberOfColumns = report.dimensions.length + report.metrics.length;

  const dateRange = useAnalyticsStoreContext((state) => state.data.first.dateRange);
  const dateRangeTwo = useAnalyticsStoreContext((state) => state.data.second.dateRange);
  const isComparing = useAnalyticsStoreContext((state) => state.isComparing);

  const { data, isLoading, error } = useData(`${report_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRange)}`, true, DEFAULT_DATA);

  const {
    data: dataTwo,
    isLoading: isLoadingTwo,
    error: errorTwo,
  } = useData(
    `${report_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
    DEFAULT_DATA,
  );

  const [mergedData, setMergedData] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchMode, setSearchMode] = useState('ALL');

  const isPages = report_id === 'pages';

  useEffect(() => {
    if (!data && data.rows.length) return;

    if (isComparing && dataTwo && dataTwo.rows.length) {
      const firstRows = data.rows
        .map(({ dimension_values, metric_values }) => ({ id: dimension_values.join('||'), dimension_values, metric_values }))
        .reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});

      const secondRows = dataTwo.rows
        .map(({ dimension_values, metric_values }) => ({ id: dimension_values.join('||'), dimension_values, metric_values_two: metric_values }))
        .reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});

      const merged = { ...firstRows };

      Object.entries(secondRows).forEach(([key, value]) => {
        merged[key] = merged[key]
          ? { ...merged[key], metric_values_two: value.metric_values_two }
          : { ...value, metric_values: Array(data.metric_headers.length).fill(null) };
      });

      // Filter out the rows that don't contain the search keyword
      const filteredRows = Object.values(merged).filter((row) => {
        const path = row.dimension_values[0];
        return searchKeyword ? path.toLowerCase().includes(searchKeyword.toLowerCase()) : true;
      });

      setMergedData({ dimension_headers: data.dimension_headers, metric_headers: data.metric_headers, rows: Object.values(filteredRows) });

      return;
    }

    // Filter out the rows that don't contain the search keyword
    const filteredRows = data.rows.filter((row) => {
      const path = row.dimension_values[0];
      return searchKeyword ? path.toLowerCase().includes(searchKeyword.toLowerCase()) : true;
    });

    setMergedData({ dimension_headers: data.dimension_headers, metric_headers: data.metric_headers, rows: filteredRows });
  }, [data, dataTwo, isComparing, searchKeyword]);

  const handleExportOnClick = () =>
    handleExport(isExporting, setIsExporting, api_v1_cms_data_exports_path, {
      data_export: {
        report_id,
        type: 'report',
        date_range_from: dayjs(dateRange.from).format('YYYY-MM-DD'),
        date_range_to: dayjs(dateRange.to).format('YYYY-MM-DD'),
        date_range_two_from: dayjs(dateRangeTwo.from).format('YYYY-MM-DD'),
        date_range_two_to: dayjs(dateRangeTwo.to).format('YYYY-MM-DD'),
      },
    });

  return (
    <StandardLayout
      h6="Insights"
      pageH1={report.title}
      button={
        <div className="d-flex gap-3 align-items-center">
          <button className="btn btn-primary" onClick={handleExportOnClick} disabled={isLoading || isLoadingTwo || isExporting}>
            Export CSV
          </button>
        </div>
      }
      subtitle="Tip: Scroll left and right on the table to see more data if it is too wide for your screen"
    >
      <div id="analytics">
        <div className="row pb-3">
          <div className="col">
            <div className={`d-flex justify-content-${isPages ? 'between' : 'end'}`}>
              {isPages && (
                <div className="d-flex gap-3 align-items-center">
                  <div>
                    {searchMode === 'SEGMENT' && (
                      <TableSegmentSearch {...{ mergedData, setMergedData, searchKeyword, setSearchKeyword, isDisabled: isLoading || isLoadingTwo }} />
                    )}
                    {searchMode === 'ALL' && (
                      <div style={{ width: 400 }}>
                        <TextInput
                          {...{
                            value: searchKeyword,
                            onChange: (e) => setSearchKeyword(e.target.value),
                            disabled: isLoading || isLoadingTwo,
                            placeholder: 'Search by keyword...',
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="vr vertical_line text-muted my-3" />
                  <span className="text-muted">Search by:</span>
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <button
                      className={`btn btn-sm btn-outline-primary ${searchMode === 'SEGMENT' ? 'active' : ''}`}
                      onClick={() => {
                        setSearchKeyword('');
                        setSearchMode('SEGMENT');
                      }}
                      disabled={isLoading || isLoadingTwo}
                    >
                      <i className="bi bi-search"></i> Segment
                    </button>
                    <button
                      className={`btn btn-sm btn-outline-primary ${searchMode === 'ALL' ? 'active' : ''}`}
                      onClick={() => {
                        setSearchKeyword('');
                        setSearchMode('ALL');
                      }}
                      disabled={isLoading || isLoadingTwo}
                    >
                      <i className="bi bi-search"></i> Keyword
                    </button>
                  </div>
                </div>
              )}
              <DateRangeComparatorInput />
            </div>
          </div>
        </div>
        <div className="card card-fill overflow-auto w-100">
          {isLoading || isLoadingTwo ? (
            <div className="d-flex align-items-center justify-content-center gap-2" style={{ height: 400 }}>
              <div className="spinner-grow spinner-grow-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="spinner-grow spinner-grow-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="spinner-grow spinner-grow-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="spinner-grow spinner-grow-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="spinner-grow spinner-grow-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error || errorTwo ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 400 }}>
              {error && (
                <div className="d-flex gap-2">
                  <span>First Dataset:</span>
                  <AnalyticsError errorMsg={error} />
                </div>
              )}
              {errorTwo && (
                <div className="d-flex gap-2">
                  <span>Second Dataset:</span>
                  <AnalyticsError errorMsg={errorTwo} />
                </div>
              )}
            </div>
          ) : (
            <table className="table table-sm card-table table-hover table-nowrap table-scrollable">
              <thead>
                <tr>
                  {report.dimensions.map((d) => (
                    <th key={d.name} className="dimension">
                      {humanizeCamelCase(d.name)}
                    </th>
                  ))}
                  {report.metrics.map((m) => (
                    <th key={m.name} className="metric">
                      {humanizeCamelCase(m.name)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!Object.values(mergedData).length ? (
                  <tr>
                    <td colSpan={numberOfColumns} className="text-center">
                      No data available for the selected date range.
                    </td>
                  </tr>
                ) : (
                  mergedData.rows.map((item, index) => (
                    <tr key={index}>
                      {item.dimension_values.map((value, index) => (
                        <td className="dimension" key={index}>
                          {value}
                        </td>
                      ))}
                      {item.metric_values.map((value, index) => (
                        <td className="metric" key={index}>
                          <div className="d-flex gap-2">
                            <span className={isComparing ? 'fs-4' : ''}>
                              {formatString(value, data.metric_headers[index].type, data.metric_headers[index].name) || '-'}
                            </span>
                            {isComparing && (
                              <div className="text-muted d-flex gap-2">
                                <div>|</div>
                                <span>
                                  {formatString(
                                    item.metric_values_two ? item.metric_values_two[index] : null,
                                    data.metric_headers[index].type,
                                    data.metric_headers[index].name,
                                  ) || '-'}
                                </span>
                                {value && item.metric_values_two && (
                                  <span>
                                    (
                                    {calculateDifference(
                                      value,
                                      item.metric_values_two ? item.metric_values_two[index] : null,
                                      data.metric_headers[index].type,
                                      data.metric_headers[index].name,
                                    )}
                                    )
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </StandardLayout>
  );
}

export default function AnalyticsTable(props) {
  return (
    <AnalyticsStoreProvider>
      <Table {...props} />
    </AnalyticsStoreProvider>
  );
}
